import { engineComponent, NumberUtils, StringUtils } from "neo-common";
import { ImageTextElement } from "./ImageTextElement";
import { ComponentTypes } from "../../ComponentTypes";
import { ITweenable, Tweens } from "../../Effects/Tweens";
import { AppContext } from "../../../../Common/AppContext";
import { ImageElementAttributes } from "../Graphics/ImageElement";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTextCounterElement extends ImageTextElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    static Round: boolean = false;

    protected from: number;

    protected to: number;

    protected duration: number;

    protected round: boolean;

    protected currentValue: number;

    protected finalValue: number;

    protected appendText: string;

    protected prependText: string;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.from = 0;
        this.to = 0;
        this.duration = 0;
        this.round = false;
        this.currentValue = 0;
        this.finalValue = 0;
        this.appendText = "";
        this.prependText = "";
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public updateByNode(node: Element): void {
        super.updateByNode(node);

        if (node.hasAttribute(ImageTextCounterElementAttributes.FROM)) {
            this.from = parseFloat(node.getAttribute(ImageTextCounterElementAttributes.FROM));
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.TO)) {
            this.to = parseFloat(node.getAttribute(ImageTextCounterElementAttributes.TO));
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.DURATION)) {
            this.duration = parseInt(node.getAttribute(ImageTextCounterElementAttributes.DURATION));
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.ROUND)) {
            this.round = StringUtils.parseBoolean(node.getAttribute(ImageTextCounterElementAttributes.ROUND));
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.APPEND_TEXT)) {
            this.appendText = node.getAttribute(ImageTextCounterElementAttributes.APPEND_TEXT);
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.PREPEND_TEXT)) {
            this.prependText = node.getAttribute(ImageTextCounterElementAttributes.PREPEND_TEXT);
        }

        if (node.hasAttribute(ImageTextCounterElementAttributes.ACTION)) {
            switch (node.getAttribute(ImageTextCounterElementAttributes.ACTION)) {
                case ImageTextCounterElementAttributes.ACTION_VALUE.START:
                    this.start(this.from, this.to, this.duration);
                    break;
                case ImageTextCounterElementAttributes.ACTION_VALUE.STOP:
                    this.stop();
                    break;
                case ImageTextCounterElementAttributes.ACTION_VALUE.FORCE:
                    this.force();
                    break;
            }
        }
    }

    public setBitmapText(value: number): void {
        this.finalValue = value;
        this.bitmapText.text = "";
        this.bitmapText.text =
            this.prependText +
            NumberUtils.valueToNumber(
                value,
                this.round || ImageTextCounterElement.Round ? 0 : AppContext.gameConfiguration.ShowDecimalPlaces,
            ) +
            this.appendText;

        this.realign();
    }

    //******************************************************************************************************************
    //  START
    //******************************************************************************************************************

    public start(from: number, to: number, duration?: number, delay?: number): void {
        const d = duration != null ? duration : this.duration;
        const del = delay != null ? delay : 0;

        if (d <= 0 || from === to) {
            this.currentValue = to;
            this.finalValue = to;

            this.onCountTo();
        } else {
            this.currentValue = from;
            this.finalValue = from;

            this.onCountTo();

            this.finalValue = to;

            Tweens.customTween(this, {
                tween: "currentValue",
                tweenValues: [this.currentValue, this.finalValue],
                tweenDuration: [d],
                tweenDelay: [del],
                callbackType: "onUpdate",
                callbackFunction: this.onCountTo,
                callbackScope: this,
            });
        }
    }

    protected onCountTo(): void {
        if (this.currentValue === this.finalValue) {
            Tweens.removeTweens(this, "currentValue");
        }

        this.bitmapText.text =
            this.prependText +
            NumberUtils.valueToNumber(
                this.currentValue,
                this.round || ImageTextCounterElement.Round ? 0 : AppContext.gameConfiguration.ShowDecimalPlaces,
            ) +
            this.appendText;

        this.realign();
    }

    //******************************************************************************************************************
    //  STOP
    //******************************************************************************************************************

    public stop(): void {
        Tweens.removeTweens(this, "currentValue");
    }

    //******************************************************************************************************************
    //  FORCE
    //******************************************************************************************************************

    public force(): void {
        this.currentValue = this.finalValue;

        this.onCountTo();
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): ImageTextCounterElement {
        let element: ImageTextCounterElement = new ImageTextCounterElement();
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;

        element.appendText = this.appendText;
        element.prependText = this.prependText;

        element.font = this.font;
        element.text = this.text;
        element.textAlign = this.textAlign;
        element.textBaseline = this.textBaseline;

        element.from = this.from;
        element.to = this.to;
        element.duration = this.duration;
        element.round = this.round;

        element.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        element.addChild(element.bitmapText);

        element.realign();
        element.markAsCloned();

        return element;
    }
}

export const ImageTextCounterElementAttributes = {
    /**
     * Round will show numbers without decimal places.
     * @property Round
     * @type boolean
     * @default false
     */
    ROUND: "Round",
    /**
     * Duration in frames. This property sets how long will counter run in frames.
     * @property Duration
     * @type number
     * @default 0
     */
    DURATION: "Duration",
    FROM: "From",
    TO: "To",
    ACTION: "Action",
    ACTION_VALUE: {
        START: "Start",
        STOP: "Stop",
        FORCE: "Force",
    },
    APPEND_TEXT: "Append",
    PREPEND_TEXT: "Prepend",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Texts/ImageTextCounterElement.ts
 **/